import React, { useEffect, useState } from 'react';
import { Search, Plus, Edit2, Trash2, ChevronDown, ChevronUp, Package, Car, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../Context/AuthContext';
import Swal from 'sweetalert2';
import { API_URL } from '../../config';

const BrandModelManager = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBrands, setExpandedBrands] = useState([]);
  const { token } = useAuth();

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/brands`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBrands(data);
      }
    } catch (err) {
      console.error('Markalar yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const toggleBrand = (brandId) => {
    setExpandedBrands(prev => 
      prev.includes(brandId) 
        ? prev.filter(id => id !== brandId) 
        : [...prev, brandId]
    );
  };

  const handleAddBrand = async () => {
    const { value: name } = await Swal.fire({
      title: 'Yeni Marka Ekle',
      input: 'text',
      inputLabel: 'Marka Adı',
      inputPlaceholder: 'Örn: Togg',
      showCancelButton: true,
      confirmButtonText: 'Ekle',
      cancelButtonText: 'İptal',
      background: '#0f172a',
      color: '#fff',
      confirmButtonColor: '#00d1ff',
      inputValidator: (value) => {
        if (!value) return 'Marka adı boş olamaz!';
      }
    });

    if (name) {
      try {
        const response = await fetch(`${API_URL}/admin/brands`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ name })
        });
        if (response.ok) {
          fetchBrands();
          Swal.fire({ icon: 'success', title: 'Eklendi', background: '#0f172a', color: '#fff' });
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Hata', text: 'İşlem başarısız.', background: '#0f172a', color: '#fff' });
      }
    }
  };

  const handleEditBrand = async (brand) => {
    const { value: name } = await Swal.fire({
      title: 'Markayı Düzenle',
      input: 'text',
      inputValue: brand.name,
      showCancelButton: true,
      confirmButtonText: 'Güncelle',
      cancelButtonText: 'İptal',
      background: '#0f172a',
      color: '#fff',
      confirmButtonColor: '#00d1ff',
    });

    if (name && name !== brand.name) {
      try {
        const response = await fetch(`${API_URL}/admin/brands/${brand.id}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ name })
        });
        if (response.ok) {
          fetchBrands();
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleDeleteBrand = async (id) => {
    const result = await Swal.fire({
      title: 'Emin misiniz?',
      text: "Bu markayı silmek markaya bağlı TÜM modelleri de silecektir!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#334155',
      confirmButtonText: 'Evet, Sil!',
      background: '#0f172a',
      color: '#fff'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/admin/brands/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          fetchBrands();
          Swal.fire({ icon: 'success', title: 'Silindi', background: '#0f172a', color: '#fff' });
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleAddModel = async (brandId, brandName) => {
    const { value: name } = await Swal.fire({
      title: `${brandName} İçin Model Ekle`,
      input: 'text',
      inputPlaceholder: 'Örn: T10X',
      showCancelButton: true,
      confirmButtonText: 'Ekle',
      background: '#0f172a',
      color: '#fff',
      confirmButtonColor: '#00d1ff',
    });

    if (name) {
      try {
        const response = await fetch(`${API_URL}/admin/brands/${brandId}/models`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ name })
        });
        if (response.ok) {
          fetchBrands();
          if (!expandedBrands.includes(brandId)) toggleBrand(brandId);
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleEditModel = async (model) => {
    const { value: name } = await Swal.fire({
      title: 'Modeli Düzenle',
      input: 'text',
      inputValue: model.name,
      showCancelButton: true,
      confirmButtonText: 'Güncelle',
      background: '#0f172a',
      color: '#fff',
    });

    if (name && name !== model.name) {
      try {
        const response = await fetch(`${API_URL}/admin/models/${model.id}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ name })
        });
        if (response.ok) fetchBrands();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleDeleteModel = async (id) => {
    const result = await Swal.fire({
      title: 'Modeli Sil?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sil',
      background: '#0f172a',
      color: '#fff'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/admin/models/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) fetchBrands();
      } catch (err) {
        console.error(err);
      }
    }
  };

  // Search Logic
  const filteredBrands = brands.filter(b => 
    b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.models.some(m => m.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Pagination Logic
  const totalPages = Math.ceil(filteredBrands.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBrands = filteredBrands.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="brand-model-content" style={{ padding: '0 0.5rem 2rem 0.5rem' }}>
      <div className="admin-header-flex" style={{ display: 'flex', flexDirection: window.innerWidth < 768 ? 'column' : 'row', justifyContent: 'space-between', alignItems: window.innerWidth < 768 ? 'flex-start' : 'center', marginBottom: '2.5rem', gap: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', fontWeight: '900', color: '#fff', marginBottom: '0.25rem', letterSpacing: '-0.02em' }}>
            Marka & Model Yönetimi
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)' }}>
            Araç kütüphanesindeki tüm marka ve modelleri hiyerarşik olarak yönetin. ({filteredBrands.length} Marka)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', width: window.innerWidth < 768 ? '100%' : 'auto', flexWrap: 'wrap' }}>

          <button 
            onClick={handleAddBrand}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1.5rem', borderRadius: '1rem', fontWeight: '800', fontSize: '0.9rem', flex: window.innerWidth < 480 ? '1.5' : 'none' }}
          >
            <Plus size={18} /> Ekle
          </button>
        </div>
      </div>

      <div style={{ position: 'relative', marginBottom: '2.5rem' }}>
        <Search style={{ position: 'absolute', left: '1.5rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)' }} size={20} />
        <input 
          type="text" 
          placeholder="Marka veya model ara..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.05)',
            borderRadius: '1.5rem',
            padding: '1.2rem 1.5rem 1.2rem 4rem',
            color: '#fff',
            fontSize: '1.1rem',
            outline: 'none',
            transition: 'all 0.3s',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
          }}
          onFocus={(e) => e.target.style.borderColor = '#00d1ff'}
          onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.05)'}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '5rem', color: 'rgba(255,255,255,0.3)' }}>Yükleniyor...</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {currentBrands.map(brand => (
              <div key={brand.id} style={{ 
                background: 'rgba(255,255,255,0.02)', 
                border: '1px solid rgba(255,255,255,0.05)', 
                borderRadius: '2rem',
                overflow: 'hidden',
                transition: 'all 0.3s'
              }}>
                <div 
                  onClick={() => toggleBrand(brand.id)}
                  style={{ 
                    padding: '1.5rem 2rem', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    cursor: 'pointer',
                    background: expandedBrands.includes(brand.id) ? 'rgba(0,209,255,0.05)' : 'transparent'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <div style={{ 
                      width: '56px', 
                      height: '56px', 
                      background: 'linear-gradient(135deg, #00d1ff, #0072ff)', 
                      borderRadius: '1.25rem', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      boxShadow: '0 8px 15px rgba(0,209,255,0.2)'
                    }}>
                      <Car color="#fff" size={28} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#fff', marginBottom: '0.2rem' }}>{brand.name}</h3>
                      <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.4)', fontWeight: '600' }}>
                        {brand.models.length} Model Kayıtlı
                      </p>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button 
                        onClick={() => handleAddModel(brand.id, brand.name)}
                        style={{ background: 'rgba(34, 197, 94, 0.1)', border: 'none', color: '#4ade80', padding: '0.75rem 1.25rem', borderRadius: '1rem', fontWeight: '700', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
                      >
                        <Plus size={16} /> Model Ekle
                      </button>
                      <button 
                        onClick={() => handleEditBrand(brand)}
                        style={{ background: 'rgba(255, 255, 255, 0.05)', border: 'none', color: '#fff', padding: '0.75rem', borderRadius: '1rem', cursor: 'pointer' }}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button 
                        onClick={() => handleDeleteBrand(brand.id)}
                        style={{ background: 'rgba(239, 68, 68, 0.1)', border: 'none', color: '#f87171', padding: '0.75rem', borderRadius: '1rem', cursor: 'pointer' }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                    <div style={{ color: '#00d1ff' }}>
                      {expandedBrands.includes(brand.id) ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </div>
                  </div>
                </div>

                {expandedBrands.includes(brand.id) && (
                  <div style={{ 
                    padding: '1rem 2rem 2.5rem 2rem', 
                    background: 'rgba(0,0,0,0.2)',
                    borderTop: '1px solid rgba(255,255,255,0.05)'
                  }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {brand.models.map(model => (
                        <div key={model.id} style={{ 
                          background: 'rgba(255,255,255,0.03)', 
                          padding: '1rem 1.25rem', 
                          borderRadius: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          border: '1px solid rgba(255,255,255,0.05)',
                          transition: 'transform 0.2s',
                        }}
                        onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                            <Package size={16} color="rgba(0,209,255,0.5)" style={{ flexShrink: 0 }} />
                            <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{model.name}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button 
                              onClick={() => handleEditModel(model)}
                              style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.3)', padding: '0.4rem', cursor: 'pointer' }}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button 
                              onClick={() => handleDeleteModel(model.id)}
                              style={{ background: 'transparent', border: 'none', color: 'rgba(239, 68, 68, 0.4)', padding: '0.4rem', cursor: 'pointer' }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                      {brand.models.length === 0 && (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.2)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                          Bu markaya ait henüz model eklenmemiş.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {filteredBrands.length === 0 && (
              <div style={{ textAlign: 'center', padding: '10rem', color: 'rgba(255,255,255,0.2)' }}>
                Aramanızla eşleşen sonuç bulunamadı.
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginTop: '3rem', paddingBottom: '1rem', flexWrap: 'wrap' }}>
              <button 
                disabled={currentPage === 1}
                onClick={() => paginate(currentPage - 1)}
                style={{ 
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', 
                  color: currentPage === 1 ? '#475569' : '#00d1ff', 
                  width: '42px', height: '42px', borderRadius: '0.75rem', 
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: '700',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
                }}
              >
                <ChevronLeft size={20} />
              </button>
              
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => paginate(i + 1)}
                    style={{
                      width: '42px', height: '42px', borderRadius: '0.75rem', border: '1px solid',
                      borderColor: currentPage === i + 1 ? '#00d1ff' : 'rgba(255,255,255,0.1)',
                      background: currentPage === i + 1 ? '#00d1ff' : 'rgba(255,255,255,0.05)',
                      color: currentPage === i + 1 ? '#000' : 'white',
                      fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s',
                      boxShadow: currentPage === i + 1 ? '0 0 15px rgba(0, 209, 255, 0.3)' : 'none',
                      transform: currentPage === i + 1 ? 'scale(1.05)' : 'scale(1)'
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button 
                disabled={currentPage === totalPages}
                onClick={() => paginate(currentPage + 1)}
                style={{ 
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', 
                  color: currentPage === totalPages ? '#475569' : '#00d1ff', 
                  width: '42px', height: '42px', borderRadius: '0.75rem', 
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: '700',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
                }}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BrandModelManager;

